import React from "react";

import { filter, find, isEmpty } from "lodash/fp";

import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import type { UserDistributionSetting } from "@app/common/types";

import { RECOMMENDATION_DISTRIBUTION_CHANNELS } from "../constants";
import DistributionChannelRow from "../DistributionChannelRow";
import ReportingSettingAccordion from "../ReportingSettingAccordion";
import { AccordionDetailsContent } from "../styles";
import * as ReportingSettingUtils from "../utils";

const DEFAULT_RECOMMENDATION_DISTRIBUTION: Omit<
  UserDistributionSetting,
  "method"
> = {
  category: "recommendation",
  isEnabled: true,
  frequency: "daily",
  sendTo: "me",
};

const RecommendationsSettings = () => {
  const { userSettings, updateUserSettings } = useUserSettings();

  const recommendationSettings = ReportingSettingUtils.getSettingsForCategory(
    userSettings,
    "recommendation"
  );

  const enabledChannelSettings = filter(
    { isEnabled: true },
    recommendationSettings
  );

  const handleToggleEnabled = (enable: boolean) => {
    const updatedSettings =
      enable && isEmpty(recommendationSettings)
        ? RECOMMENDATION_DISTRIBUTION_CHANNELS.map(({ channel }) => ({
            ...DEFAULT_RECOMMENDATION_DISTRIBUTION,
            method: channel,
          }))
        : recommendationSettings.map((setting) => ({
            ...setting,
            isEnabled: enable,
          }));

    updateUserSettings.mutate({ userDistributionSettings: updatedSettings });
  };

  const handleToggleChannelEnabled = (
    channel: UserDistributionSetting["method"],
    enable: boolean,
    existingSetting?: UserDistributionSetting
  ) => {
    const updatedSetting = existingSetting
      ? { ...existingSetting, isEnabled: enable }
      : {
          ...DEFAULT_RECOMMENDATION_DISTRIBUTION,
          method: channel,
          isEnabled: enable,
        };

    updateUserSettings.mutate({ userDistributionSettings: [updatedSetting] });
  };

  return (
    <ReportingSettingAccordion
      title="Recommendations"
      subtitle="Notify me when Read generates personalized suggestions and action items"
      enabledChannels={enabledChannelSettings}
      onToggleEnabled={handleToggleEnabled}
    >
      <AccordionDetailsContent>
        {RECOMMENDATION_DISTRIBUTION_CHANNELS.map((channel) => {
          const userSetting = find(
            { method: channel.channel },
            recommendationSettings
          );

          return (
            <DistributionChannelRow
              key={channel.channel}
              config={channel}
              name="recommendations-distribution"
              defaultFrequency={DEFAULT_RECOMMENDATION_DISTRIBUTION.frequency}
              defaultSendTo={DEFAULT_RECOMMENDATION_DISTRIBUTION.sendTo}
              onToggleEnabled={handleToggleChannelEnabled}
              setting={userSetting}
            />
          );
        })}
      </AccordionDetailsContent>
    </ReportingSettingAccordion>
  );
};

export default RecommendationsSettings;
